import { Button, cn, ghostAccentIconTileClass } from '@up2stay/up2promo-ui';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';

import {
  REPUTATION_SECTION_MUTED_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useNotificationCapabilities } from '@/core/hooks/use-notification-capabilities';
import { usePushPermissionStatus } from '@/core/hooks/use-push-permission-status';
import { completePushGrantFlow } from '@/core/lib/push-permission-flow';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

type ReputationPushCtaProps = {
  dict: {
    title: string;
    description: string;
    enable: string;
    openSettings: string;
    denied: string;
    unavailable: string;
  };
};

export function ReputationPushCta({ dict }: ReputationPushCtaProps) {
  const { navigateShell } = useNavigateShell();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { data: capabilities } = useNotificationCapabilities();
  const pushFeatureEnabled = capabilities?.pushEnabled === true;
  const { status: permissionStatus, refresh } = usePushPermissionStatus(
    pushFeatureEnabled && Boolean(workspaceId),
  );

  if (
    !pushFeatureEnabled ||
    !isCapacitorNative() ||
    !workspaceId ||
    permissionStatus === null ||
    permissionStatus === 'granted'
  ) {
    return null;
  }

  const handleEnable = async () => {
    const granted = await completePushGrantFlow(workspaceId);
    if (!granted) {
      toast.error(dict.denied);
      await refresh();
      return;
    }

    await refresh();
    toast.success(dict.title);
    navigateShell(`${shellPaths.moreUserNotifications}?tab=preferences`);
  };

  return (
    <div className="flex flex-col gap-3 rounded-[var(--r-card)] border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-3">
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
            ghostAccentIconTileClass,
          )}
          aria-hidden
        >
          <Bell className="h-4 w-4" />
        </span>
        <div className="space-y-1">
          <p className={REPUTATION_SECTION_TITLE_CLASS}>{dict.title}</p>
          <p className={REPUTATION_SECTION_MUTED_CLASS}>{dict.description}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => void handleEnable()}>
          {dict.enable}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            navigateShell(`${shellPaths.moreUserNotifications}?tab=preferences`)
          }
        >
          {dict.openSettings}
        </Button>
      </div>
    </div>
  );
}
